var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Left, Maybe, Nothing } from 'purify-ts';
import { Component, Prop, Watch } from 'vue-property-decorator';
import * as tsx from 'vue-tsx-support';
import { modifiers as m } from 'vue-tsx-support';
import * as utils from '@/utils';
import { numberInputValue } from './utils';
let NumberInput = class NumberInput extends tsx.Component {
    constructor() {
        super(...arguments);
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this.userInput = '';
    }
    onValueChanged() {
        this.value.ifRight(maybeValue => {
            const value = maybeValue.extractNullable();
            if (value == null) {
                this.userInput = '';
            }
            else if (value !== this.internalValue.orDefault(Nothing).extractNullable()) {
                if (this.maxDecimals == null) {
                    this.userInput = value.toString(10);
                }
                else {
                    this.userInput = utils.toMaxNDecimals(value, this.maxDecimals);
                }
            }
        });
    }
    emitValue(_, oldValue) {
        const isImmediateEmit = oldValue == null;
        if (!isImmediateEmit || this.internalValue.isLeft()) {
            tsx.emitOn(this, 'onInput', this.internalValue);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get internalValue() {
        const parsed = utils.parseOrKeepFloat(this.userInput);
        if (!this.userInput) {
            if (this.required) {
                return Left(new Error(`${this.name} may not be empty.`));
            }
            else {
                return numberInputValue(null);
            }
        }
        else if (Number.isNaN(parsed)) {
            return Left(new Error(`${this.name} is not a number.`));
        }
        else if (parsed < this.min) {
            return Left(new Error(`${this.name} should be greater than or equal to ${this.min}.`));
        }
        else if (parsed > this.max) {
            return Left(new Error(`${this.name} should be less than or equal to ${this.max}.`));
        }
        else {
            return this.validate(parsed).toEither(Maybe.fromNullable(parsed)).swap();
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    decValue() {
        this.maybeUpdate(v => v - this.step);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    incValue() {
        this.maybeUpdate(v => v + this.step);
    }
    /**
     * Runs the updater on the `internalValue` if the component is not disabled and the value
     * has not errored.
     *
     * @param f The updater function.
     */
    maybeUpdate(f) {
        if (!this.disabled) {
            this.internalValue.ifRight(maybeValue => maybeValue.ifJust(value => {
                const newValue = Math.max(this.min, Math.min(this.max, f(value)));
                this.userInput = newValue.toString(10);
            }));
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    render() {
        const handleUp = m.up.prevent(this.incValue);
        const handleDown = m.down.prevent(this.decValue);
        return (<input v-model={this.userInput} onKeydown={event => {
                handleUp(event);
                handleDown(event);
            }} placeholder={utils.coerceToString(this.placeholder)} class={['number-input', this.inputClass]} name={this.name} disabled={this.disabled} type="tel"/>);
    }
};
__decorate([
    Prop({ required: true })
], NumberInput.prototype, "value", void 0);
__decorate([
    Prop({ type: String, default: 'tel' })
], NumberInput.prototype, "type", void 0);
__decorate([
    Prop({ type: Number, default: 1 })
], NumberInput.prototype, "step", void 0);
__decorate([
    Prop({ type: Number, default: -Infinity })
], NumberInput.prototype, "min", void 0);
__decorate([
    Prop({ type: Number, default: Infinity })
], NumberInput.prototype, "max", void 0);
__decorate([
    Prop({ type: String, default: 'Value' })
], NumberInput.prototype, "name", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], NumberInput.prototype, "required", void 0);
__decorate([
    Prop({ type: Number, default: null })
], NumberInput.prototype, "maxDecimals", void 0);
__decorate([
    Prop({ type: [String, Number], default: undefined })
], NumberInput.prototype, "placeholder", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], NumberInput.prototype, "disabled", void 0);
__decorate([
    Prop({ type: [String, Array], default: 'form-control text-right' })
], NumberInput.prototype, "inputClass", void 0);
__decorate([
    Prop({ type: Function, default: () => Maybe.empty() })
], NumberInput.prototype, "validate", void 0);
__decorate([
    Watch('value', { immediate: true })
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], NumberInput.prototype, "onValueChanged", null);
__decorate([
    Watch('userInput', { immediate: true })
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], NumberInput.prototype, "emitValue", null);
NumberInput = __decorate([
    Component
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], NumberInput);
export default NumberInput;
